import { format } from 'date-fns';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth/services';

import { ViLPRProvider } from '../../shared/models';
import { getBoundingBox } from '../../utils/utils';
import { LPR_ERRORS, LprWarningEntry } from '../constants/errors';
import { IFsHistoricalProviderResultsInterface } from '../interfaces';
import { IFsLprParameters } from '../interfaces/common/parameters';
import { IFsLprProviderInterface } from '../interfaces/common/provider';
import { IFsLprResult } from '../interfaces/common/result';
import { IFsLprVigilantParameters } from '../interfaces/lpr-vigilant-parameters.interface';
import { VigilantResponse } from '../models/vigilant-response.model';
import { LprReadsAPI } from '../services';

// Vigilant requires plate, so Partial<> needs to keep it required
export type PartialVigilantParams = Partial<Omit<IFsLprVigilantParameters, 'plate'>> & {
  plate: string;
};

@Injectable({ providedIn: 'root' })
export class VigilantProviderService implements IFsLprProviderInterface {
  public name = 'Vigilant';
  public nameEnum?: ViLPRProvider;

  public get loaded$(): Observable<boolean> {
    return this._loaded$;
  }

  public get historicLoaded$(): Observable<boolean> {
    return this._historicLoaded$;
  }

  public warnings: LprWarningEntry[] = [];
  public errors: string[] = [];

  public isLoading: boolean;
  public isInit: boolean;
  public isFinished: boolean = false;

  private _params: IFsLprVigilantParameters;
  private _loaded$: Subject<boolean> = new Subject<boolean>();
  private _historicLoaded$: Subject<boolean> = new Subject<boolean>();
  private _inited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _destroy$: Subject<void> = new Subject<void>();
  private readonly _cancel$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _lprReadsService: LprReadsAPI,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    if (this._sdmEnabled) {
      this.nameEnum = ViLPRProvider.VIGILANT;
      this.isLoading = true;
      this.isInit = false;
      this.total = 0;
    }
  }

  public results: IFsLprResult[] = [];
  public total: number = 0;

  /**
   * Initializes the service
   */
  public init(): void {
    this._inited$.next(true);
    this.isInit = true;
  }

  /**
   * Starts a search for LPR records using the provided parameters
   * @param query - The search parameters
   */
  public search(query: Partial<IFsLprParameters>): void {
    const inited$ = this._sdmEnabled
      ? this._inited$.pipe(
          filter((inited) => inited),
          take(1),
        )
      : this._inited$;
    inited$.subscribe((inited) => {
      if (inited) {
        this._startSearch(query);
      }
    });
  }

  /**
   * Fetches the next page of results
   * Does nothing if already loading or all data has been retrieved
   */
  public next(): void {
    if (this.isFinished || this.isLoading || this.errors.length > 0) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // TODO: FWX-1888 - Standardize/debug search pagination across providers; Validate if this is doing anything and remove if not
    this._params.page++;
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    this._getPlates(this._params);
  }

  public getHistoricalResults(
    searchParams: Partial<IFsLprParameters>,
  ): Observable<IFsHistoricalProviderResultsInterface> {
    const modifiedParams = this._historicalSearchTransformParams(searchParams);

    return this._lprReadsService.getVigilantPlates(modifiedParams).pipe(
      map((response) => {
        return {
          results: response.results || [],
          total: response.results.length || 0,
        };
      }),
      catchError(() => {
        return of(null);
      }),
      takeUntil(this._destroy$),
    );
  }

  public cancel(): void {
    this._cancel$.next();
  }

  private get _sdmEnabled(): boolean {
    return this._currentOrganization?.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  /**
   * Starts a new search operation
   * @param query - The search parameters
   * @private
   */
  private _startSearch(query: Partial<IFsLprParameters>): void {
    this.cancel(); // Cancel any ongoing search
    this.errors = [];
    this.warnings = [];
    this.results = [];
    this.total = 0;
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }

    const params = this._searchTransformParams(query);
    if (this.errors.length > 0) {
      this.isLoading = false;
      this._loaded$.next(true);
      this.total = 0;
      return;
    }
    this._getPlates(params);
  }

  /**
   * Processes the API response and updates the service state
   * @param response - The response from the Vigilant API
   * @private
   */
  private _handleResponse(response: VigilantResponse): void {
    if (response) {
      this.total = response.results.length;
      this.results = [...this.results, ...response.results];
      this.isFinished = this.results.length >= this.total;
      this.isLoading = false;
      this._loaded$.next(true);
    }
  }

  private _baseTransformParams(params: Partial<IFsLprParameters>): PartialVigilantParams {
    const { plate, fromDate, toDate, inBbox } = params;

    // Note: Vigilant API does not paginate and returns all results at once
    const baseParams: PartialVigilantParams = {
      plate,
      from_date: fromDate ? format(new Date(fromDate), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX") : null,
      to_date: toDate ? format(new Date(toDate), "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX") : null,
      in_bbox:
        getBoundingBox(inBbox, this._sdmEnabled && this.nameEnum ? this.nameEnum : this.name) ||
        null,
    };

    return baseParams;
  }

  /**
   * Transforms the generic parameters to Vigilant-specific format
   * @param params - The generic search parameters
   * @returns Vigilant-specific parameters
   * @private
   */
  private _searchTransformParams(params: Partial<IFsLprParameters>): IFsLprVigilantParameters {
    // Vigilant API requires a plate to be provided - this is a blocking error that prevents searching
    if (!params.plate) {
      this.errors.push(LPR_ERRORS.PLATE_REQUIRED);
    }

    // Check for unsupported filters and add warnings
    // Vigilant API does not support plate state
    if (params.plateState) {
      this.warnings.push({
        type: 'PLATE_STATE_NOT_SUPPORTED',
        message: LPR_ERRORS.PLATE_STATE_NOT_SUPPORTED,
      });
    }

    // Vigilant API does not support vehicle color
    if (params.vehicleColor?.length) {
      this.warnings.push({
        type: 'VEHICLE_COLOR_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_COLOR_NOT_SUPPORTED,
      });
    }

    // Vigilant API does not support vehicle make
    if (params.vehicleMake) {
      this.warnings.push({
        type: 'VEHICLE_MAKE_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_MAKE_NOT_SUPPORTED,
      });
    }

    // Vigilant API does not support vehicle body
    if (params.vehicleBody) {
      this.warnings.push({
        type: 'VEHICLE_BODY_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_BODY_NOT_SUPPORTED,
      });
    }

    if (params.assignedUserIds?.length) {
      this.warnings.push({
        type: 'ASSIGNED_USER_IDS_NOT_SUPPORTED',
        message: LPR_ERRORS.ASSIGNED_USER_IDS_NOT_SUPPORTED,
      });
    }

    if (params.deviceNames?.length) {
      this.warnings.push({
        type: 'DEVICE_NAMES_NOT_SUPPORTED',
        message: LPR_ERRORS.DEVICE_NAMES_NOT_SUPPORTED,
      });
    }

    // Only include supported parameters in baseParams
    const supportedBaseParams = this._baseTransformParams({
      plate: params.plate,
      fromDate: params.fromDate,
      toDate: params.toDate,
      inBbox: params.inBbox,
    });

    this._params = supportedBaseParams;

    return this._preparePayload(this._params);
  }

  private _historicalSearchTransformParams(
    params: Partial<IFsLprParameters>,
  ): IFsLprVigilantParameters {
    return this._baseTransformParams(params);
  }

  /**
   * Removes empty values from the payload
   * @param payload - The parameters to clean
   * @returns Cleaned parameters object
   * @private
   */
  private _preparePayload(payload: IFsLprVigilantParameters): IFsLprVigilantParameters {
    Object.keys(payload).forEach((key) => {
      if (!payload[key] || payload[key] === '') {
        delete payload[key];
      }
    });
    return {
      ...payload,
    };
  }

  /**
   * Fetches plate data from the Vigilant API
   * @param params - The parameters for the API request
   * @private
   */
  private _getPlates(params: IFsLprVigilantParameters): void {
    this.isLoading = true;
    this._lprReadsService
      .getVigilantPlates(params)
      .pipe(
        takeUntil(this._cancel$),
        catchError(() => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_SEARCH_RESULTS);
          this.isFinished = true;
          this.isLoading = false;
          // clearing results on error b/c of no pagination from Vigilant API
          this.results = [];
          this.total = 0;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }
}
